import Icon from '@mui/material/Icon'
import React from 'react'

import Button from '@/app/component/atom/button'
import { type CampaignItem } from '@/app/module/campaigns/types'

import { canResume } from './helpers'

type Props = {
  campaign: Pick<CampaignItem, 'open' | 'published' | 'type'>
  isLoading: boolean
  isMobile: boolean
  resumableSubIds: number[]
  selectedContactIds: number[]

  onResume: (subscriptionId: number) => Promise<void>
  onSubscribe: (contactIds: number[]) => Promise<void>
  onUnsubscribe: (contactIds: number[]) => Promise<void>
}

const ContactActions: React.FC<Props> = (props) => {
  const { campaign, isLoading, isMobile, resumableSubIds, selectedContactIds, onResume, onSubscribe, onUnsubscribe } =
    props
  const selectedCount = selectedContactIds.length
  const canResumeSelectedSubscription = selectedCount === 1 && resumableSubIds.length === 1
  const subscribeDisabledReason = getSubscribeContactsDisabledReason({
    campaignOpen: campaign.open,
    campaignPublished: campaign.published,
    isLoading,
    selectedCount,
  })
  const unsubscribeDisabledReason = getUnsubscribeContactsDisabledReason({
    campaignPublished: campaign.published,
    isLoading,
    selectedCount,
  })
  const resumeDisabledReason = getResumeSubscriptionDisabledReason({
    campaignOpen: campaign.open,
    campaignPublished: campaign.published,
    canResumeSelectedSubscription,
    isLoading,
    selectedCount,
  })

  const handleSubscribeSelected = React.useCallback(
    () => onSubscribe(selectedContactIds),
    [selectedContactIds, onSubscribe],
  )
  const handleUnsubscribeSelected = React.useCallback(
    () => onUnsubscribe(selectedContactIds),
    [selectedContactIds, onUnsubscribe],
  )
  const handleResumeSelected = React.useCallback(() => {
    if (!canResumeSelectedSubscription) {
      return
    }

    return onResume(resumableSubIds[0])
  }, [canResumeSelectedSubscription, resumableSubIds, onResume])

  return (
    <>
      <Button
        disabled={!!subscribeDisabledReason}
        startIcon={isMobile ? undefined : <Icon>input</Icon>}
        tooltip={subscribeDisabledReason}
        variant="outlined"
        onClick={handleSubscribeSelected}
      >
        {isMobile ? 'Subscribe' : 'Subscribe Contacts'}
      </Button>
      <Button
        disabled={!!unsubscribeDisabledReason}
        startIcon={isMobile ? undefined : <Icon>remove_circle</Icon>}
        tooltip={unsubscribeDisabledReason}
        variant="outlined"
        onClick={handleUnsubscribeSelected}
      >
        {isMobile ? 'Unsubscribe' : 'Unsubscribe Contacts'}
      </Button>
      {canResume(campaign.type) && (
        <Button
          disabled={!!resumeDisabledReason}
          startIcon={isMobile ? undefined : <Icon>play_circle_filled</Icon>}
          tooltip={resumeDisabledReason}
          variant="outlined"
          onClick={handleResumeSelected}
        >
          {isMobile ? 'Resume' : 'Resume Subscription'}
        </Button>
      )}
    </>
  )
}

const getLoadingDisabledReason = (isLoading: boolean) =>
  isLoading ? 'Please wait until the current subscription action is finished.' : ''

const getSubscribeContactsDisabledReason = ({
  campaignOpen,
  campaignPublished,
  isLoading,
  selectedCount,
}: {
  campaignOpen: boolean
  campaignPublished: boolean
  isLoading: boolean
  selectedCount: number
}) => {
  if (!campaignPublished) {
    return 'The campaign must be live before you can subscribe contacts.'
  }

  if (!campaignOpen) {
    return 'Please "Enable new subscriptions" in the Actions menu on the Campaigns page to be able to subscribe contacts.'
  }

  if (selectedCount === 0) {
    return 'Please select a contact.'
  }

  return getLoadingDisabledReason(isLoading)
}

const getUnsubscribeContactsDisabledReason = ({
  campaignPublished,
  isLoading,
  selectedCount,
}: {
  campaignPublished: boolean
  isLoading: boolean
  selectedCount: number
}) => {
  if (!campaignPublished) {
    return 'The campaign must be live before you can unsubscribe contacts.'
  }

  if (selectedCount === 0) {
    return 'Please select a contact.'
  }

  return getLoadingDisabledReason(isLoading)
}

const getResumeSubscriptionDisabledReason = ({
  campaignOpen,
  campaignPublished,
  canResumeSelectedSubscription,
  isLoading,
  selectedCount,
}: {
  campaignOpen: boolean
  campaignPublished: boolean
  canResumeSelectedSubscription: boolean
  isLoading: boolean
  selectedCount: number
}) => {
  if (!campaignPublished) {
    return 'The campaign must be live before you can resume subscriptions.'
  }

  if (!campaignOpen) {
    return 'Campaign has to be opened to be able to resume subscriptions.'
  }

  if (selectedCount === 0 || selectedCount > 1 || !canResumeSelectedSubscription) {
    return 'Please select a finished IVR, SMS, or WhatsApp subscription.'
  }

  return getLoadingDisabledReason(isLoading)
}

export default ContactActions
